import { useEffect, useState, type ReactNode } from 'react';
import { RefreshCw, Smartphone } from 'lucide-react';
import type { PaigeDocumentOrigin } from './mobile-types';
import { capturedWhen, mapLink, placeWords, rescanWords, senderName } from './mobile-words';
import './capture-origin.css';

/**
 * Above the fields, for a document sent from a phone (docs/engineering/mobile-capture.md, section 7):
 * "From Jane Smith · Paige Mobile · Oct 4, 9:12 AM · 27.951, -82.457 (±12 m)", the place a map link
 * followed only by the reviewer's own click, with no referrer; and "Ask Jane to scan it again", with an
 * optional note, which her phone is told at once. Nothing at all for a document that came another way.
 * Mounted with the document id as its key, so a new document starts afresh.
 */
export function CaptureOrigin({ documentId }: { documentId: string | null }): ReactNode {
  const channel = window.paigeWorkbench?.mobile;
  const [origin, setOrigin] = useState<PaigeDocumentOrigin | null>(null);
  useEffect(() => {
    if (!channel || !documentId) return undefined;
    let live = true;
    channel
      .origin(documentId)
      .then((found) => live && setOrigin(found))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [channel, documentId]);
  if (!origin || !channel || !documentId) return null;
  return (
    <div className="paige-review-notice paige-capture-origin" role="note">
      <Smartphone size={14} aria-hidden="true" />
      <OriginWords origin={origin} />
      {origin.canAskRescan && (
        <RescanAsk
          origin={origin}
          ask={(note) => channel.rescan(documentId, note)}
          onAsked={setOrigin}
        />
      )}
    </div>
  );
}

function OriginWords({ origin }: { origin: PaigeDocumentOrigin }): ReactNode {
  return (
    <span>
      From {senderName(origin)} · Paige Mobile · {capturedWhen(origin.capturedAt)}
      {origin.location && (
        <>
          {' · '}
          <a
            href={mapLink(origin.location)}
            target="_blank"
            rel="noreferrer noopener"
            referrerPolicy="no-referrer"
          >
            {placeWords(origin.location)}
          </a>
        </>
      )}
      {origin.rescan && <small>{rescanWords(origin.rescan)}</small>}
    </span>
  );
}

/** "Ask Jane to scan it again", then a note and Ask. */
function RescanAsk({
  origin,
  ask,
  onAsked,
}: {
  origin: PaigeDocumentOrigin;
  ask: (note: string) => Promise<PaigeDocumentOrigin>;
  onAsked: (origin: PaigeDocumentOrigin) => void;
}): ReactNode {
  const [asking, setAsking] = useState(false);
  if (!asking)
    return (
      <button type="button" className="quiet-button" onClick={() => setAsking(true)}>
        <RefreshCw size={13} aria-hidden="true" /> Ask {senderName(origin).split(' ')[0]} to scan it
        again
      </button>
    );
  return <AskForm ask={ask} onAsked={onAsked} onCancel={() => setAsking(false)} />;
}

/** The note and Ask; the API's refusal under it. */
function AskForm({
  ask,
  onAsked,
  onCancel,
}: {
  ask: (note: string) => Promise<PaigeDocumentOrigin>;
  onAsked: (origin: PaigeDocumentOrigin) => void;
  onCancel: () => void;
}): ReactNode {
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const send = () =>
    ask(note).then(onAsked, (error: unknown) =>
      setProblem(error instanceof Error ? error.message : String(error)),
    );
  return (
    <span className="paige-capture-ask">
      <input
        type="text"
        maxLength={200}
        value={note}
        placeholder="What to fix (optional)"
        aria-label="What to fix"
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      <button type="button" className="quiet-button" onClick={() => void send()}>
        Ask
      </button>
      <button type="button" className="quiet-button" onClick={onCancel}>
        Cancel
      </button>
      {problem && <small role="alert">{problem}</small>}
    </span>
  );
}
